<template>
  <b-card>
    <McTable
      ref="vehicle-table-ref"
      searchable
      class="table-select-vehicle"
      :fields="fields"
      :apiClient="apiClient"
      :activeFilters="activeFilters"
      :hasDelete="false"
      @handleSelectValue="handleSelectVehicle"
    >
      <template #beforeSearchBar>
        <McTableFilter
          :activeFilters="activeFilters"
          :parseFilterDataMappings="parseFilterDataMappings"
          @submit="handleChangeActiveFilters"
        >
          <template
            #content="{
              formValues: filterValues,
              updateFormValues,
              fullObjects,
              updateFullObjects,
            }"
          >
            <InputTableFilter
              :formValues="filterValues"
              :fullObjects="fullObjects"
              @update:formValues="updateFormValues($event)"
              @update:fullObjects="updateFullObjects($event)"
            />
          </template>
        </McTableFilter>
      </template>

      <template v-slot:[`cell(license_plate)`]="{ item }">
        {{ item?.license_plate ?? '-' }}
        <div class="text-muted" v-if="item?.hull_no">
          {{ item?.hull_no }}
        </div>
      </template>

      <template #cell(vehicle_group)="{ value }">
        {{ joinVehicleGroupName(value) }}
      </template>
    </McTable>
  </b-card>
</template>

<script>
import McTable from '@/components/McTable'
import McTableFilter from '@/components/McTableFilter'

import InputTableFilter from './components/InputTableFilter'
import parseFilterDataMappings from './components/InputTableFilter/lib/parse-filter-data-mappings.js'
import filterMappings from '../../lib/constants/filter-mappings.js'

import * as Vehicle from '@/services/rest-api/lib/route-optimization/vehicle.js'
import getVehicleFields from '../../lib/fields/vehicle-list-fields.js'
import { EventBus } from '../../lib/event-bus.js'
import { mapGetters, mapState } from 'vuex'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McTable,
    McTableFilter,
    InputTableFilter,
  },
  mixins: [Helper],
  props: {
    vehicleReoptimizeIds: {
      type: Array,
      default: () => [],
    },
    userGroupId: {
      type: [Number, String],
      default: null,
    },
    userGroupVgroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeFilters: {},
      parseFilterDataMappings,
      isUserSelection: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapState('access', ['vGroups']),
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read'])
    },
    fields() {
      return getVehicleFields(this)
    },
    apiClient() {
      let queryParamFilters = []

      Object.keys(this.activeFilters).forEach((key) => {
        const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
        if (filter) {
          queryParamFilters.push(filter)
        }
      })

      if (this.hasTmsUserGroupAccess) {
        const vgroups = this.isSuperUser() ? this.userGroupVgroups : this.vGroups
        if (Array.isArray(vgroups) && vgroups.length > 0) {
          const vgroupIds = vgroups.map((vg) => vg.id).join(',')
          queryParamFilters.push(`vehicle_group.id in (${vgroupIds})`)
        }
      }

      let getVehicle = Vehicle.get(queryParamFilters)

      return {
        get: getVehicle,
      }
    },
  },
  watch: {
    vehicleReoptimizeIds: {
      handler(newIds) {
        if (this.isUserSelection) {
          this.isUserSelection = false
          return
        }

        this.$nextTick(() => {
          this.setTableSelection(newIds)

          if (newIds && newIds.length > 0) {
            EventBus.$emit('update-form-values', {
              key: 'vehicle_ids',
              value: [...newIds],
            })
          }
        })
      },
      immediate: false,
    },
    userGroupId: {
      handler(newId) {
        this.$nextTick(() => {
          const tableRef = this.$refs['vehicle-table-ref']
          this.ResetTableState()
          if (tableRef && typeof tableRef.getData === 'function') {
            tableRef.getData({
              limit: tableRef.limit,
              page: 1,
              search: tableRef.search || undefined,
              sorts: tableRef.sorts.map((s) => `${s.key} ${s.order}`),
            })
          }
        })
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.vehicleReoptimizeIds.length > 0) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.setTableSelection(this.vehicleReoptimizeIds)
        }, 500)
      })
    }
  },
  methods: {
    setTableSelection(ids) {
      if (!this.$refs['vehicle-table-ref'] || !ids || ids.length === 0) {
        return
      }

      const selectedIds = {}
      ids.forEach((id) => {
        selectedIds[Number(id)] = true
      })

      this.$refs['vehicle-table-ref'].selectedIds = selectedIds
      this.$refs['vehicle-table-ref'].selectedIdsLength = ids.length
    },

    ResetTableState() {
      this.activeFilters = {}
      this.isUserSelection = false
      this.$refs['vehicle-table-ref'].ResetSelects()
    },

    joinVehicleGroupName(groups) {
      if (groups.length === 0) {
        return '-'
      }

      let groupNames = []
      groups.forEach((item) => {
        groupNames.push(item.name)
      })

      return groupNames.join(', ')
    },

    handleSelectVehicle(selectedIds) {
      this.isUserSelection = true

      const value = []

      for (const [key, val] of Object.entries(selectedIds)) {
        if (!val) continue
        value.push(Number(key))
      }

      EventBus.$emit('update-form-values', { key: 'vehicle_ids', value })
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)

        if (filter.values === null || filter.values === undefined || filter.values === '') return
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
  },
}
</script>
